import { CHAT_STATUS_TO_ACCOUNT_STATUS } from '@/experiences/sales-portal/domain/accounts/screens/account-list/blocs/transformers/account-list-transformers';
import { useGeneratedSalesPortalAccountListFilterCountsQuery } from '@/experiences/sales-portal/domain/accounts/screens/account-list/blocs/documents/sales-portal-account-list-filter-counts.generated';
import { useMemo } from 'react';
import type {
  AccountStatus,
  RestaurantGroup,
} from '@/experiences/sales-portal/domain/accounts/screens/account-list/types';
import type { SalesPortalAccountListFilters } from '@/types';

interface UseAccountListFilterCountsLoaderParams {
  businessOrganizationUUID: string;
  filters?: SalesPortalAccountListFilters;
}

interface UseAccountListFilterCountsLoaderResult {
  groupCounts?: Map<string, number>;
  restaurantGroups: RestaurantGroup[];
  statusCounts?: Map<AccountStatus, number>;
}

export function useAccountListFilterCountsLoader({
  businessOrganizationUUID,
  filters,
}: UseAccountListFilterCountsLoaderParams): UseAccountListFilterCountsLoaderResult {
  const { data, previousData } =
    useGeneratedSalesPortalAccountListFilterCountsQuery({
      variables: {
        businessOrganizationUUID,
        filters,
      },
    });

  const counts = (data ?? previousData)?.getSalesPortalAccountListFilterCounts;

  const groupCounts = useMemo(() => {
    if (!counts) {
      return undefined;
    }
    return new Map(
      counts.group_counts.map(({ group_id, count }) => [group_id, count]),
    );
  }, [counts]);

  const restaurantGroups = useMemo<RestaurantGroup[]>(() => {
    if (!counts) {
      return [];
    }
    return counts.group_counts.map(({ group_id, name }) => ({
      name,
      uuid: group_id,
    }));
  }, [counts]);

  const statusCounts = useMemo(() => {
    if (!counts) {
      return undefined;
    }
    const map = new Map<AccountStatus, number>();
    for (const { status, count } of counts.status_counts) {
      const accountStatus = CHAT_STATUS_TO_ACCOUNT_STATUS[status];
      if (accountStatus) {
        map.set(accountStatus, count);
      }
    }
    return map;
  }, [counts]);

  return { groupCounts, restaurantGroups, statusCounts };
}
